import { useRef, useEffect, useCallback } from 'react'
import SignaturePad from 'signature_pad'
import type { QuestionProps } from '../eform.types'

/**
 * Drawn signature capture for question type 11.
 *
 * This reproduces legacy FTP's ENCODING configuration exactly, because that is
 * the only configuration measured in production: single-digit KB for a typical
 * signature and ~51 KB for its largest, against the 65,535-byte TEXT ceiling on
 * surveys_answers.responseContent.
 *
 * Legacy constructs the pad with NO options at all
 * (html/lib/forms2-survey-body.php:891, signature_pad 2.3.2 from a CDN) on an
 * 800x150 canvas. So: transparent background, black pen, default stroke widths.
 * Do not add constructor options -- every deviation there has cost us once
 * already, and the DO NOT SET note below is the scar.
 *
 * The one intentional difference is CANVAS_HEIGHT (200 rather than 150), for a
 * better signing target. Canvas dimensions do not change how the ink is encoded,
 * so that is a safe place to differ; the constructor options are not.
 *
 * WHY THIS MATTERS
 * ----------------
 * The pad originally hand-rolled its drawing, stroking each mousemove segment
 * separately, and produced ~45 KB signatures -- 69% of the ceiling at rest. An
 * elaborate signature crossed it, truncated silently on write (the legacy DB is
 * not in strict mode) and rendered as a broken image on the patient Forms tab.
 * Nothing catches this at submit time: while signing the patient sees live canvas
 * strokes, so the stored bytes are not decoded as a real PNG until someone opens
 * the form afterwards.
 *
 * DO NOT SET backgroundColor.
 * An attempt to set it to opaque white -- on the theory that antialiased strokes
 * blending against a solid colour would compress better than varying alpha --
 * made things WORSE, taking a test signature to 52 KB. On a transparent canvas
 * ink pixels are (r, g, b, alpha) with RGB constant at the pen colour and only
 * alpha varying: one channel of entropy. Against opaque white the antialiased
 * edge instead varies across all three colour channels at different rates, and
 * PNG has three channels of gradient to encode rather than one.
 */

/**
 * Canvas height in CSS pixels. The BITMAP is sized to this exactly -- see
 * syncBitmapToCssSize() for why the two must agree.
 *
 * This is the ONE deliberate departure from legacy's 800x150, kept because the
 * taller pad is a better signing target. It costs very little: the extra 50px is
 * empty transparent space, and PNG compresses uniform runs to almost nothing --
 * a signature's bytes are overwhelmingly its antialiased ink, which a taller
 * canvas does not change. (A taller box may invite physically larger signatures,
 * which is real ink and does cost bytes, but that is a UX consequence rather
 * than a geometric one.)
 *
 * Unlike the constructor options above, this does not touch how the ink is
 * encoded, so it cannot reintroduce the compression blow-up that the white
 * background caused.
 */
const CANVAS_HEIGHT = 200

/**
 * Widest the pad may get, matching legacy. Applied as CSS so the bitmap, which
 * tracks offsetWidth, is bounded by the same number.
 */
const MAX_CANVAS_WIDTH = 800

/**
 * Largest base64 string we are willing to store, in bytes.
 *
 * Deliberately BELOW the 65,535-byte TEXT ceiling on
 * surveys_answers.responseContent rather than equal to it. The margin covers the
 * fact that a signature stops being a lone value the moment it is written: it is
 * also inlined into the rendered form HTML archived in comm_log.notes, and it
 * rides inside the JSON submit body. Sitting flush against the ceiling is what
 * caused this bug in the first place.
 *
 * If the column is ever widened to MEDIUMTEXT this can rise a long way, and the
 * ladder below will simply stop firing for real signatures.
 */
const EXPORT_BUDGET_BYTES = 55000

/**
 * Resolutions to try, best first. Full size unless full size does not fit.
 *
 * The point of the ladder is that reduction is the exception, not the rule. Over
 * ten years legacy produced exactly one signature near the ceiling, so virtually
 * every signature exports at 1.0 and is stored at full fidelity -- the common
 * path is a single toDataURL, exactly as before. Only a signature that would
 * otherwise be SILENTLY TRUNCATED gets stepped down, and a slightly soft
 * signature is strictly better than half a PNG that renders as a broken image.
 *
 * This is why the ladder beats an unconditional downscale: it spends image
 * quality only where the alternative is losing the signature altogether.
 */
const EXPORT_SCALE_LADDER = [1, 0.75, 0.6, 0.5, 0.4, 0.3]

/**
 * Downscale a canvas, halving repeatedly until within one step of the target.
 *
 * A single drawImage straight down to a much smaller size undersamples thin
 * antialiased strokes and visibly chews up a signature; stepping in halves keeps
 * the ink intact. Worth the extra passes -- they run once per finished stroke,
 * not per pointer move.
 */
function downscaleCanvas(src: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const targetW = Math.max(1, Math.round(src.width * scale))
  const targetH = Math.max(1, Math.round(src.height * scale))

  let cur: HTMLCanvasElement = src
  while (cur.width / 2 > targetW) {
    const half = document.createElement('canvas')
    half.width = Math.round(cur.width / 2)
    half.height = Math.round(cur.height / 2)
    const hctx = half.getContext('2d')
    if (!hctx) return src
    hctx.imageSmoothingEnabled = true
    hctx.imageSmoothingQuality = 'high'
    hctx.drawImage(cur, 0, 0, half.width, half.height)
    cur = half
  }

  const out = document.createElement('canvas')
  out.width = targetW
  out.height = targetH
  const octx = out.getContext('2d')
  if (!octx) return src
  octx.imageSmoothingEnabled = true
  octx.imageSmoothingQuality = 'high'
  octx.drawImage(cur, 0, 0, targetW, targetH)
  return out
}

/**
 * Encode the pad at the highest resolution that fits within the byte budget.
 *
 * Returns full resolution for anything that fits, which is nearly everything.
 * Steps down only for a signature dense enough that storing it whole would
 * overflow the column and be silently truncated.
 *
 * If even the smallest rung overflows, the smallest is returned anyway: an
 * over-budget but COMPLETE image may still be truncated by the database, but it
 * is the best available outcome and never worse than not shrinking at all.
 */
function exportWithinBudget(canvas: HTMLCanvasElement): string {
  let smallest = ''
  for (const scale of EXPORT_SCALE_LADDER) {
    const source = scale === 1 ? canvas : downscaleCanvas(canvas, scale)
    const url = source.toDataURL('image/png')
    smallest = url
    if (url.length <= EXPORT_BUDGET_BYTES) {
      return url
    }
  }
  return smallest
}

export default function SignatureField({ value, onChange, error }: QuestionProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const padRef = useRef<SignaturePad | null>(null)

  // onChange gets a fresh identity on every render of the parent renderer. Held
  // in a ref so the effect below can stay mount-only -- listing it as a dep
  // would tear down and rebuild the pad mid-signature.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Restored once, on mount. Re-running this whenever `value` changes would
  // repaint over the patient while they are still drawing.
  const initialValueRef = useRef(value)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // No options, exactly as legacy does it. See the note above before adding any.
    const pad = new SignaturePad(canvas)
    padRef.current = pad

    /**
     * Size the bitmap to the canvas's CSS size at a strict 1:1 ratio.
     *
     * signature_pad's _createPoint uses raw CSS coordinates (x - rect.left) and
     * applies no bitmap/rect scaling of its own, so if the two disagree every
     * stroke lands offset from the pen.
     *
     * Deliberately NOT multiplied by devicePixelRatio, which is what
     * components/payments/SignaturePadField.tsx does. On a retina screen that
     * quadruples the pixel count and the base64 PNG along with it. Legacy draws
     * at 1:1 and nobody has asked for a crisper pad.
     */
    const syncBitmapToCssSize = () => {
      const width = canvas.offsetWidth
      // A hidden or not-yet-laid-out container reports 0; sizing to that would
      // throw the bitmap away for nothing. Branching form logic can mount a
      // signature question while it is still hidden, which is why this is
      // watched with a ResizeObserver rather than a window resize listener --
      // becoming visible does not fire a window resize.
      if (width <= 0) return
      if (canvas.width === width && canvas.height === CANVAS_HEIGHT) return

      // Assigning to width/height wipes the bitmap. redraw() replays both the
      // cached fromDataURL image and the recorded strokes, so a restored
      // signature survives a rotation or an address-bar collapse on a phone.
      canvas.width = width
      canvas.height = CANVAS_HEIGHT
      pad.redraw()
    }

    syncBitmapToCssSize()

    const initial = initialValueRef.current
    // `value` is string | string[] across all question types; only a single
    // data URL is meaningful for a signature.
    if (typeof initial === 'string' && initial !== '') {
      // Restores a signature drawn earlier in this session. The previous
      // implementation destructured `value` and never used it, so paging back to
      // a signed question showed an empty pad even though the answer was still
      // held in state. ratio must be pinned to 1: fromDataURL defaults it to
      // devicePixelRatio, which would redraw at half size on a retina screen
      // given we deliberately draw at 1:1.
      void pad.fromDataURL(initial, { ratio: 1 }).catch(() => {
        // A stored value that will not decode (an already-truncated signature,
        // say) must not take the whole form down -- leave the pad blank so the
        // patient can simply sign again.
      })
    }

    const handleEndStroke = () => {
      // The visible pad is never touched -- the patient signs at full size and
      // sees their own strokes. Only the exported copy is ever reduced, and only
      // when full size would not fit.
      onChangeRef.current(exportWithinBudget(canvas))
    }
    pad.addEventListener('endStroke', handleEndStroke)

    // Setting the width/height attributes changes the bitmap, not the CSS box,
    // so this cannot feed back into itself.
    const observer = new ResizeObserver(syncBitmapToCssSize)
    observer.observe(canvas)

    return () => {
      pad.removeEventListener('endStroke', handleEndStroke)
      observer.disconnect()
      pad.off()
      padRef.current = null
    }
  }, [])

  const clearSignature = useCallback(() => {
    padRef.current?.clear()
    onChange('')
  }, [onChange])

  return (
    <div>
      <div className="space-y-1">
        <div className={`rounded-lg border overflow-hidden ${error ? 'border-red-300' : 'border-slate-200'}`}>
          <canvas
            ref={canvasRef}
            style={{ height: `${CANVAS_HEIGHT}px`, maxWidth: `${MAX_CANVAS_WIDTH}px` }}
            className="block w-full bg-white cursor-crosshair touch-none"
          />
        </div>
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-slate-400">Sign above</span>
          <button
            type="button"
            onClick={clearSignature}
            className="text-xs text-slate-500 hover:text-red-500 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}
